import { useState, useMemo, useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import Select from 'react-select'

import toastService from '@/components/toast/toast-service'
import Loader from '@/components/Loader'
import { usePlataformContext } from '@/contexts/platform.context'
import { useGetBranchesData } from '@/hooks/queries/use-get-branch-data'
import { useGetUserData } from '@/hooks/queries/use-get-user-data'
import { setUserBranch } from '@/services/branches'
import { TokenStorage } from '@/utils/token-storage'

const ChangeBranchIframe = ({ token, AppUrl, onLoad }: { token: string, AppUrl: string, onLoad: () => void }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const authUrlSet = useRef(false)

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.src = `${AppUrl}/ApiAuth/AuthenticateFromMicroservice?token=${token}`
      authUrlSet.current = true
    }
  }, [token])

  const handleLoad = () => {
    if (authUrlSet.current) {
      onLoad()
    }
  }

  return (
    <iframe
      ref={iframeRef}
      width={250}
      height={250}
      className='hidden overflow-hidden'
      title='Change Branch Authentication'
      onLoad={handleLoad}
    />
  )
}

const SelectBranch = () => {
  const [token, setToken] = useState('')
  const [iframeRendered, setIframeRendered] = useState(false)
  const [isChangingBranch, setIsChangingBranch] = useState(false)
  const { iframeRef } = usePlataformContext()
  const { data: branches, isLoading } = useGetBranchesData()
  const { data: userData } = useGetUserData()
  const queryClient = useQueryClient()
  const AppUrl = process.env.VITE_APP_ONIX_URL
  const returnPathRef = useRef('')

  const handleSelectedBranch = async (selectedOption: any) => {
    returnPathRef.current = window.location.pathname + window.location.search
    if (iframeRef.current) {
      iframeRef.current.src = 'about:blank'
    }
    setIsChangingBranch(true)
    const selectedBranchId = selectedOption.value
    try {
      const data = await setUserBranch(selectedBranchId)
      TokenStorage.storeToken(data.token.token)
      setToken(data.token.token)
      setIframeRendered(true)
    } catch (error) {
      console.log(error)
      setIsChangingBranch(false)
    } finally {
      queryClient.clear()
    }
  }

  const handleIframeLoad = () => {
    if (iframeRef.current) {
      iframeRef.current.src = `${AppUrl}${returnPathRef.current}`
    }
    setIframeRendered(false)
    setIsChangingBranch(false)
    toastService.success('Unidade alterada.')
  }

  const mapedData = useMemo(() => {
    return branches?.map((item: any) => ({
      value: item.branchId,
      label: `${item.name} - ${item.cnpj}`
    }))
  }, [branches])

  const selectedOption = mapedData?.find(
    option => option.value === userData?.authenticatedBranchId
  )

  return (
    <div className='w-full max-w-[250px]'>
      {isChangingBranch && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-70'>
          <Loader size='xl' />
        </div>
      )}
      {iframeRendered && <ChangeBranchIframe token={token} AppUrl={AppUrl} onLoad={handleIframeLoad} />}
      <Select
        id='branches'
        onChange={handleSelectedBranch}
        isDisabled={isLoading || isChangingBranch}
        value={selectedOption}
        options={mapedData}
        className='text-gray-900 text-sm rounded-lg focus:ring-red-500 focus:border-red-500 w-full p-2 max-w-[400px] min-w-[120px]'
        isSearchable
        escapeClearsValue
        isClearable={false}
        noOptionsMessage={() => 'Sem resultados'}
        loadingMessage={() => 'Carregando...'}
        placeholder='Selecione...'
        styles={{
          menu: base => ({
            ...base,
            width: '139%'
          })
        }}
      />
    </div>
  )
}

export default SelectBranch
